import { Box, Typography } from "components";
import dayjs from "dayjs";
import { ConsentItem, ConsentOrder } from "./useMFConsent";
import { moneyCommaSeparator } from "utils";

interface Props {
  order: ConsentOrder;
  item: ConsentItem;
}

const InfoColumn = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
    <Typography color="var(--neutral-500)" fontSize={12} fontWeight={500}>
      {label}
    </Typography>
    <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
      {value || "-"}
    </Typography>
  </Box>
);

const formatFrequency = (f: string | null | undefined) =>
  f ? f.charAt(0).toUpperCase() + f.slice(1).replace(/_/g, " ") : "-";

const planTypeLabel = (orderType: string, transactionType: string): string => {
  const order = orderType.charAt(0).toUpperCase() + orderType.slice(1);
  if (transactionType === "lumpsum") return `${order} - Lumpsum`;
  const planMap: Record<string, string> = {
    sip: "Plan (SIP)",
    swp: "Plan (SWP)",
    stp: "Plan (STP)",
  };
  return `${order} - ${planMap[transactionType] ?? transactionType}`;
};

const ConsentOrderCard = ({ order, item }: Props) => {
  const isPlan = ["sip", "swp", "stp"].includes(order.transactionType);
  const isSwitch = order.orderType === "switch";

  const placedDate = order.startDate ?? order.scheduledFor;

  return (
    <Box
      sx={{
        bgcolor: "var(--grey-bg-100)",
        borderRadius: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        p: "20px",
      }}
    >
      <Typography color="var(--black-900)" fontSize={14} fontWeight={700}>
        {planTypeLabel(order.orderType, order.transactionType)}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: { xs: "16px", sm: "32px" },
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            sm: "repeat(auto-fit, minmax(120px, max-content))",
          },
        }}
      >
        {isSwitch ? (
          <>
            <InfoColumn label="Switch Out Scheme" value={item.schemeName} />
            <InfoColumn
              label="Switch In Scheme"
              value={item.switchInSchemeName}
            />
          </>
        ) : (
          <InfoColumn label="Scheme Name" value={order.schemeName} />
        )}

        {item.portfolioName ? (
          <InfoColumn label="Portfolio" value={item.portfolioName} />
        ) : null}

        <InfoColumn label="Folio Number" value={item.folioNumber} />

        {item.amount != null ? (
          <InfoColumn
            label="Amount"
            value={moneyCommaSeparator(+item.amount)}
          />
        ) : (
          <InfoColumn label="Units" value={item.units} />
        )}

        {isPlan && (
          <>
            <InfoColumn
              label="Frequency"
              value={formatFrequency(order.frequency)}
            />
            <InfoColumn
              label="No. of Installments"
              value={
                order.isPerpetual
                  ? "Perpetual"
                  : order.installments != null
                    ? String(order.installments)
                    : "-"
              }
            />
          </>
        )}

        <InfoColumn
          label="Order Placed Date"
          value={placedDate ? dayjs(placedDate).format("DD/MM/YYYY") : "-"}
        />
      </Box>
    </Box>
  );
};

export default ConsentOrderCard;
